import { useHandler } from "@redotech/react-util/hook";
import { useOptimizationSettings } from "@redotech/redo-merchant-app-common/support/use-optimization-settings";
import { ShopperAiSettingsOptimizationType } from "@redotech/redo-model/shopper-ai-settings-optimization";
import { toast } from "@redotech/redo-web/alert";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { memo } from "react";
import {
  GenericSettingsOptimizationBanner,
  SettingToOptimize,
} from "../optimization-banners/generic-settings-optimization-banner";
import {
  checkBaseEligibility,
  OptimizationProps,
  SettingsOptimizationDefinition,
} from "../optimization-types";

const BundlesOptimizationBanner = memo(function BundlesOptimizationBanner({
  handleComplete,
  handleDismiss,
}: OptimizationProps) {
  const {
    settings,
    mutation,
    handleApplyAll,
    componentLevelDismiss,
    isLoading,
  } = useOptimizationSettings({
    applyAllRecommendations: {
      "concierge/skills/productBundle/enabled": true,
      "concierge/skills/productBundle/maxDiscountPercentage": 10,
      "concierge/skills/productBundle/discountCodeExpirationMinutes": 10_080,
    },
    handleComplete,
    handleDismiss,
    shouldCompleteOnAnyChange: true,
  });

  const handleUpdate = useHandler(async (enabled: boolean) => {
    await mutation.mutateAsync({
      "concierge/skills/productBundle/enabled": enabled,
    });
    toast("Settings applied successfully", { variant: "success" });
  });

  const handleMaxDiscountUpdate = useHandler(async (value: string) => {
    const numValue = Number(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 10) {
      await mutation.mutateAsync({
        "concierge/skills/productBundle/maxDiscountPercentage": numValue,
      });
    }
  });

  const handleExpirationUpdate = useHandler(async (value: string) => {
    const days = Number(value);
    if (!isNaN(days) && days >= 0) {
      await mutation.mutateAsync({
        "concierge/skills/productBundle/discountCodeExpirationMinutes":
          days * 24 * 60,
      });
    }
  });

  if (isLoading || !settings) {
    return null;
  }

  const maxDiscountPercentage =
    settings["concierge/skills/productBundle/maxDiscountPercentage"] ?? 10;
  const expirationMinutes =
    settings["concierge/skills/productBundle/discountCodeExpirationMinutes"] ??
    10_080;
  const expirationDays = Math.round(expirationMinutes / (24 * 60));

  const settingsConfig: SettingToOptimize[] = [
    {
      title: "Enable product bundles",
      description:
        "Enable AI to offer bundle discounts on commonly purchased items to increase sales and customer loyalty.",
      control: (
        <Flex align="center" gap="sm">
          <RedoToggle
            setValue={handleUpdate}
            value={settings["concierge/skills/productBundle/enabled"] ?? false}
          />
        </Flex>
      ),
      onUpdate: () =>
        handleUpdate(
          settings["concierge/skills/productBundle/enabled"] ?? false,
        ),
    },
    {
      title: "Maximum discount percentage",
      description:
        "Maximum discount percentage AI can apply to bundles (0-10%)",
      control: (
        <RedoTextInput
          inputTypeHint="number"
          max={10}
          min={0}
          placeholder="10"
          setValue={handleMaxDiscountUpdate}
          size="sm"
          step={1}
          value={String(maxDiscountPercentage)}
        />
      ),
      onUpdate: () => handleMaxDiscountUpdate(String(maxDiscountPercentage)),
    },
    {
      title: "Discount code duration",
      description: "How long discount codes remain active (in days)",
      control: (
        <RedoTextInput
          inputTypeHint="number"
          min={0}
          placeholder="7"
          setValue={handleExpirationUpdate}
          size="sm"
          step={1}
          value={String(expirationDays)}
        />
      ),
      onUpdate: () => handleExpirationUpdate(String(expirationDays)),
    },
  ];

  return (
    <GenericSettingsOptimizationBanner
      handleComplete={handleComplete}
      handleDismiss={componentLevelDismiss}
      onApplyAll={handleApplyAll}
      settings={settingsConfig}
    />
  );
});

export const bundlesOptimization: SettingsOptimizationDefinition = {
  type: ShopperAiSettingsOptimizationType.BUNDLES_OPTIMIZATION,
  priority: 1,
  retries: 2,
  isEligible: async (team, user) => {
    if (!checkBaseEligibility(user)) {
      return false;
    }

    return (
      team.settings.returns?.enabled === true &&
      !team.settings.concierge.skills?.productBundle?.enabled
    );
  },
  component: BundlesOptimizationBanner,
};
